// src/layout/MainLayout.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";

import Navbar from "../components/Navbar";
import HeroSectionB2B from "../components/HeroSectionB2B";
import ServicesThreePillars from "../components/ServicesThreePillars";
import HenkiLabsSection from "../components/HenkiLabsSection";
import CommunitySection from "../components/CommunitySection";
import Footer from "../components/Footer";
import BackgroundVisuals from "../components/BackgroundVisuals";
import ScrollToTop from "../components/ScrollToTop";

// Modal diferido: no carga hasta que el usuario hace click en una app
const VibesAppModal = React.lazy(() => import("../components/VibesAppModal"));

import appItems from "../data/AppsItems";

const MainLayout = () => {
  const [selectedApp, setSelectedApp] = useState(null);

  const whatsappNumber = "584228180393"; 
  const whatsappMessage = encodeURIComponent("¡Hola! Vengo desde la web de Henki. Me gustaría obtener más información sobre sus servicios de ingeniería.");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <div className="relative bg-slate-50 text-slate-900 dark:bg-[#0d1b22] dark:text-white min-h-screen overflow-x-hidden transition-colors duration-300">
      <BackgroundVisuals />

      {/* NAVBAR */}
      <Navbar />

      <main>
        {/* ========================================================
            BLOQUE 1: HERO B2B & BARRA DE ALIADOS INTEGRADA
            ======================================================== */}
        <HeroSectionB2B />

        {/* LÍNEA DIVISORA ELEGANTE */}
        <div className="w-full max-w-7xl mx-auto px-6 py-2">
          <div className="relative h-[2px] overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
            <motion.div
              className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent"
              animate={{ x: ["-120%", "320%"] }}
              transition={{
                duration: 2.8,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          </div>
        </div>

        {/* ========================================================
            BLOQUE 2: SERVICIOS CONSOLIDADOS EN 3 PILARES
            (Auditoría 24h & Rescate, QA Google Play, Desarrollo a Medida)
            ======================================================== */}
        <ServicesThreePillars />

        {/* ========================================================
            BLOQUE 3: HENKI LABS (+100K USUARIOS & APPS PROPIAS)
            (Noise, Pomodoros, Lockly)
            ======================================================== */}
        <HenkiLabsSection
          apps={appItems}
          onSelectApp={(app) => setSelectedApp(app)}
        />

        {/* ========================================================
            BLOQUE 4: COMUNIDAD & ACADEMIA SOBRIA
            (YouTube @henkivzla, Instagram & Enlace a /cursos)
            ======================================================== */}
        <CommunitySection />
      </main>

      {/* FOOTER */}
      <Footer />

      {/* BOTÓN FLOTANTE DE WHATSAPP */}
      <motion.a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-[0_4px_18px_rgba(37,211,102,0.4)] cursor-pointer outline-none border-none"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{ y: [0, -6, 0] }}
        transition={{
          y: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
        aria-label="Contactar por WhatsApp"
      >
        <svg
          className="w-8 h-8 text-white fill-current"
          viewBox="0 0 448 512"
        >
          <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
        </svg>
      </motion.a>

      {/* BOTÓN FLOTANTE SCROLL TO TOP */}
      <ScrollToTop />

      {/* VIBES STYLE APP MODAL (Cargado en Suspense solo cuando se abre) */}
      {selectedApp && (
        <React.Suspense fallback={null}>
          <VibesAppModal
            isOpen={Boolean(selectedApp)}
            onClose={() => setSelectedApp(null)}
            app={selectedApp}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default MainLayout;